/** Billing & usage - a tab of Settings that only the company Admin has.
 *
 * **There is no billing in this product, and this screen does not pretend there is.** No plan, no
 * price, no invoice, no payment method and no payment provider exists (decisions P-06 and P-12:
 * the invoice is raised by hand, outside the product, from the signed agreement). So there is no
 * "Pay" button here, no plan picker and no amount owed, and the screen says in its last panel who
 * handles invoices and how to reach them.
 *
 * What it does show is **only what the product records**, each fact with the place it comes from
 * printed beside it (`data-source`), so an Admin can tell a figure the product measured from one
 * an operator typed after a contract was signed:
 *
 * - **Entitlements** (an operator's record, from the agreement): the AI assistant add-on, with the
 *   contract reference it was recorded against, and whether real customer documents are permitted.
 *   The data region. The screen says plainly that no plan name is recorded.
 * - **In use** (measured): people who can sign in, by role, from `/members`; evidence stored and
 *   ledger entries, from `/billing/usage`; and the month's AI spend, from the assistant's own
 *   spend read, in the currency that read states (the workspace's currency from `/config` when
 *   nothing has been spent and none is stated).
 *
 * **Each read stands alone.** One failing says so and offers a retry, and prints no figure for
 * itself - in particular no `0`: a count of zero produced by a dropped connection is the defect
 * this product keeps shipping. A workspace that really has nothing in it shows a measured zero,
 * which is a different DOM from a failure.
 *
 * **A non-Admin is told whose screen this is and nothing is requested** (the server would refuse
 * every read). No amount is converted, summed or compared here; the spend is the server's exact
 * decimal string.
 */
import { Receipt } from "@phosphor-icons/react";

import { useAssistantSpend } from "../api/assistant";
import type { AssistantSpend } from "../api/assistant";
import { useMe } from "../api/auth";
import { useBillingUsage } from "../api/billing";
import type { BillingUsage } from "../api/billing";
import { useConfig } from "../api/config";
import { ROLE_ORDER, tallyMembers, todayUtc } from "../api/licences";
import { useReader } from "../api/profile";
import { useMembers } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { NotYourRole } from "../components/NotYourRole";
import { Empty, Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import { PageHeaderCard } from "../components/ui";
import { regionWords } from "../lib/customerWords";
import { instantDate, quantity, storedSize } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import own from "./Billing.module.css";
import { ADMIN_ONLY } from "../shell/whoCan";
import styles from "./Screen.module.css";
import { ScreenFrame } from "./ScreenFrame";
import { useScreen } from "./useScreen";

const ROLE_LABEL: Record<string, string> = {
  admin: "Admins",
  approver: "Approvers",
  contributor: "Contributors",
  auditor: "Auditors",
  viewer: "Viewers",
};

/** One fact and where it comes from. `source` is printed, not a tooltip: a figure whose origin is
 * hidden behind a hover is a figure a keyboard or a phone cannot check. */
function Fact({
  rowKey,
  label,
  source,
  children,
  detail,
}: {
  rowKey: string;
  label: string;
  source: string;
  children: React.ReactNode;
  detail?: React.ReactNode;
}) {
  return (
    <tr className={own.fact} data-billing-row={rowKey}>
      <th scope="row" className={own.label}>{label}</th>
      <td className={own.value}>{children}</td>
      <td className={own.detail}>{detail ?? ""}</td>
      <td className={own.source} data-source={rowKey}>
        Source: {source}
      </td>
    </tr>
  );
}

/** A count, grouped, from an integer the server counted. */
function count(n: number): string {
  return quantity(String(n));
}

// ------------------------------------------------------------------------ entitlements

/** A recorded moment in the reader's zone. **Its own component** so the reader's preferences are
 * asked for only when there is a moment to place, never for a workspace with nothing recorded. */
function RecordedAt({ at }: { at: string }) {
  const reader = useReader();
  return <>{instantDate(at, reader)}</>;
}

function EntitlementRows({ usage }: { usage: BillingUsage }) {
  return (
    <>
      <Fact
        rowKey="plan"
        label="Plan"
        source="what the product records for a workspace"
        detail="EmitIQ keeps no plan name, price or billing period for a workspace. What you are charged is in your signed agreement."
      >
        <span data-testid="billing-plan">Not recorded in the product</span>
      </Fact>
      <Fact
        rowKey="assistant-addon"
        label="AI assistant add-on"
        source="recorded by an EmitIQ operator from your signed agreement"
        detail={
          usage.assistant_addon_entitled ? (
            <span data-testid="billing-addon-ref">
              Contract reference {usage.assistant_addon_contract_ref ?? "not recorded"}
              {usage.assistant_addon_entitled_at ? (
                <>
                  , recorded <RecordedAt at={usage.assistant_addon_entitled_at} />
                </>
              ) : null}
              .
            </span>
          ) : (
            <span data-testid="billing-addon-ref">
              No contract reference: the add-on is not in your agreement.
            </span>
          )
        }
      >
        <StatusPill tone={usage.assistant_addon_entitled ? "ok" : "neutral"} testId="billing-addon">
          {usage.assistant_addon_entitled ? "In your agreement" : "Not in your agreement"}
        </StatusPill>
      </Fact>
      <Fact
        rowKey="real-documents"
        label="Real customer documents"
        source="recorded by an EmitIQ operator after counsel's sign-off"
        detail={
          usage.real_documents_permitted && usage.real_documents_permitted_at ? (
            <span>
              Recorded <RecordedAt at={usage.real_documents_permitted_at} />.
            </span>
          ) : undefined
        }
      >
        <StatusPill tone={usage.real_documents_permitted ? "ok" : "neutral"} testId="billing-real-documents">
          {usage.real_documents_permitted ? "Permitted" : "Not permitted yet"}
        </StatusPill>
      </Fact>
      <Fact rowKey="data-region" label="Data region" source="set when the workspace was provisioned">
        <span data-testid="billing-region" title={usage.data_region}>{regionWords(usage.data_region)}</span>
      </Fact>
    </>
  );
}

// ------------------------------------------------------------------------ in use

function Entitlements({ usage }: { usage: ReturnType<typeof useBillingUsage> }) {
  return (
    <tbody data-testid="billing-entitlements" aria-label="Plan and entitlements">
      {usage.error || usage.isPending || usage.data === undefined ? (
        <tr>
          <td colSpan={4}>
            <FromUsage usage={usage} of="The plan and entitlements">
              {() => null}
            </FromUsage>
          </td>
        </tr>
      ) : (
        <EntitlementRows usage={usage.data} />
      )}
    </tbody>
  );
}

function PeopleFact() {
  const members = useMembers(true);
  return (
    <Fact rowKey="people" label="People who can sign in" source="People & access (the members of this workspace)">
      {members.error ? (
        <ReadFailed of="The member count" error={members.error} onRetry={() => void members.refetch()} />
      ) : members.isPending || members.data === undefined ? (
        <Empty>Loading…</Empty>
      ) : (
        <PeopleCounted members={members.data} />
      )}
    </Fact>
  );
}

function PeopleCounted({ members }: { members: NonNullable<ReturnType<typeof useMembers>["data"]> }) {
  const tally = tallyMembers(members, todayUtc());
  const roles = [
    ...ROLE_ORDER,
    ...Object.keys(tally.byRole)
      .filter((role) => !(ROLE_ORDER as readonly string[]).includes(role))
      .sort(),
  ];
  return (
    <span data-testid="billing-people" data-state="measured" data-count={tally.active}>
      <strong>{count(tally.active)}</strong>
      <span className={own.mix}>
        {" "}
        ({roles.map((role) => `${ROLE_LABEL[role] ?? role} ${count(tally.byRole[role] ?? 0)}`).join(", ")})
      </span>
    </span>
  );
}

function SpendFact() {
  const spend = useAssistantSpend(true);
  const config = useConfig();
  return (
    <Fact
      rowKey="ai-spend"
      label="AI spend this month"
      source="the AI assistant's spend record (AI assistant tab)"
      detail={
        spend.data ? (
          <span data-testid="billing-spend-basis">{spend.data.basis} The month is the calendar month in UTC.</span>
        ) : undefined
      }
    >
      {spend.error ? (
        <ReadFailed of="This month's AI spend" error={spend.error} onRetry={() => void spend.refetch()} />
      ) : spend.isPending || spend.data === undefined ? (
        <Empty>Loading…</Empty>
      ) : (
        <SpendFigure spend={spend.data} workspaceCurrency={config.data?.market.currency} />
      )}
    </Fact>
  );
}

function SpendFigure({ spend, workspaceCurrency }: { spend: AssistantSpend; workspaceCurrency: string | undefined }) {
  // The currency the spend read states (the Admin's provider rates are in one); with none stated
  // nothing has been priced, and the workspace's own currency is the honest unit for a zero.
  const currency = spend.currency ?? workspaceCurrency ?? null;
  return (
    <span data-testid="billing-spend" data-state="measured">
      <strong>{currency ? `${currency} ${spend.month_to_date}` : spend.month_to_date}</strong>
    </span>
  );
}

/** The `/billing/usage` half of a row, with the read's own failure in place of a figure. */
function FromUsage({
  usage,
  of,
  children,
}: {
  usage: ReturnType<typeof useBillingUsage>;
  of: string;
  children: (value: BillingUsage) => React.ReactNode;
}) {
  if (usage.error) return <ReadFailed of={of} error={usage.error} onRetry={() => void usage.refetch()} />;
  if (usage.isPending || usage.data === undefined) return <Empty>Loading…</Empty>;
  return <>{children(usage.data)}</>;
}

function InUse({ usage }: { usage: ReturnType<typeof useBillingUsage> }) {
  return (
    <tbody data-testid="billing-in-use" aria-label="In use">
      <PeopleFact />
      <Fact
        rowKey="evidence"
        label="Evidence stored"
        source="the evidence library (sizes recorded when each document was accepted)"
      >
        <FromUsage usage={usage} of="The evidence stored">
          {(value) => (
            <span data-testid="billing-evidence" data-state="measured" data-bytes={value.evidence_bytes}>
              <strong>{storedSize(value.evidence_bytes)}</strong>
              <span className={own.mix}>
                {" "}
                in {count(value.document_count)} {value.document_count === 1 ? "document" : "documents"}
              </span>
            </span>
          )}
        </FromUsage>
      </Fact>
      <Fact rowKey="ledger" label="Audit ledger entries" source="the audit ledger (entries appended to this workspace's chain)">
        <FromUsage usage={usage} of="The ledger entry count">
          {(value) => (
            <span data-testid="billing-ledger" data-state="measured" data-count={value.ledger_entries}>
              <strong>{count(value.ledger_entries)}</strong>
            </span>
          )}
        </FromUsage>
      </Fact>
      <SpendFact />
    </tbody>
  );
}

// ------------------------------------------------------------------------ invoices and payment

function PaymentPanel() {
  const config = useConfig();
  const email = config.data?.support_email ?? null;
  return (
    <Panel title="Invoices and payment" sub="outside the product" column="1 / 13">
      <p className={styles.note} data-testid="billing-outside">
        Invoices and payment are handled by Raidii LLC outside the product for now:{" "}
        {config.isPending ? (
          <span>loading the contact address…</span>
        ) : config.error ? (
          <span data-testid="billing-contact-failed">
            the contact address could not be read, so ask whoever sent you your agreement.{" "}
            <button type="button" className={own.retry} onClick={() => void config.refetch()}>
              Try again
            </button>
          </span>
        ) : email ? (
          <>
            contact{" "}
            <a href={`mailto:${email}`} data-testid="billing-contact">
              {email}
            </a>
            .
          </>
        ) : (
          <span data-testid="billing-contact-none">
            no contact address has been set for this deployment yet, so ask whoever sent you your agreement.
          </span>
        )}
      </p>
      <p className={styles.note} data-testid="billing-not-built">
        No invoice list, payment method, plan change or amount owed here, and no payment provider is connected.
        What you are charged is in your signed agreement.
      </p>
    </Panel>
  );
}

// ------------------------------------------------------------------------ the screen

/** `embedded` is the Billing and usage tab of the Admin place: the same panels in its frame. */
function BillingBody({ embedded = false }: { embedded?: boolean }) {
  const screen = useScreen();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const usage = useBillingUsage(isAdmin);

  const panels = (
    <>
      <Panel title="Plan and entitlements" sub="and what is in use, as the product records it" column="1 / 13">
        <div className={own.scroll} role="region" tabIndex={0} aria-label="Plan, entitlements and use, scrollable">
          <table className={own.facts} data-testid="billing-facts" aria-label="Plan and entitlements, and what is in use">
            <Entitlements usage={usage} />
            <InUse usage={usage} />
          </table>
        </div>
      </Panel>
      <PaymentPanel />
    </>
  );

  if (embedded) return panels;

  if (!isAdmin) {
    return (
      <ScreenFrame screen={screen} title="Billing and usage" crumb="what this workspace has and uses">
        <Panel title="Billing and usage" column="1 / 8">
          <p className={styles.note} data-testid="billing-admin-only">
            <NotYourRole inline needs={ADMIN_ONLY} /> Ask an Admin of this workspace.
          </p>
        </Panel>
      </ScreenFrame>
    );
  }

  return (
    <ScreenFrame screen={screen} title="Billing and usage" crumb="what this workspace has and uses" bodyScrolls>
      <PageHeaderCard icon={Receipt} title="Billing and usage" label="What the product records about this workspace" />
      {panels}
    </ScreenFrame>
  );
}

export function BillingContent() {
  return <BillingBody embedded />;
}

export function BillingScreen() {
  return (
    <RequireSession>
      <BillingBody />
    </RequireSession>
  );
}
